import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../Layout/Layout.jsx';
import InstructionStep from '../InstructionStep/InstructionStep.jsx';
import RecordStep from '../RecordStep/RecordStep.jsx';
import styles from './MultiStageRecord.module.css';

/**
 * Generate stages based on applicant type
 * @param {string} applicantType - 'first-time' or 'returning'
 * @returns {Array} - Array of stage objects
 */
const getStagesForApplicantType = (applicantType) => {
  const isFirstTime = applicantType === 'first-time';
  
  return [
    {
      stage: 1,
      stageName: 'introduction',
      stageTitle: 'Introduction & Background',
      description: 'Introduce yourself (name, school, study area), your service member/first responder (branch/department, relationship), and share who you are beyond the basics.',
      tips: [
        'Use the highest-quality camera available on your phone or computer',
        'If using a phone, record horizontally (landscape)',
        'Make sure your camera lens is clean and image is in focus',
        'Use a stable surface or tripod',
        'Film in a well-lit space with natural light facing you',
        'Choose a quiet space with minimal background noise',
        'Position the camera at eye level and look directly at it',
        'Speak clearly at a comfortable pace',
        'Aim for 2-3 minutes total per video'
      ],
      prompts: [
        'State your first and last name, where you\'re from',
        'Your school and area of study',
        'Your military or first responder family member, their branch/department, and relationship to you',
        'Tell us about yourself - what experiences, interests, or values have shaped who you are today?',
        'Share a story or memory about your service member/first responder. What makes you most proud?',
        'What is one value you learned from them, and how does it guide your life today?'
      ]
    },
    {
      stage: 2,
      stageName: 'education',
      stageTitle: isFirstTime ? 'Looking Ahead' : 'Growth & Impact',
      description: isFirstTime 
        ? 'As you prepare for college, what are you most excited or hopeful about? How does education connect to your future?'
        : 'How has being a Folds of Honor recipient influenced your academic journey or personal growth?',
      tips: [
        'Be yourself and speak naturally',
        'Share a specific moment or memory, not just a résumé',
        'Show impact - tell us why this scholarship matters now',
        'It\'s okay to pause, take a breath, or restart if needed'
      ],
      prompts: isFirstTime 
        ? [
            'What are you most excited about as you prepare for college?',
            'How does education connect to your future goals and dreams?',
            'What impact do you hope to make with your education?'
          ]
        : [
            'How has being a Folds of Honor recipient influenced your academic journey?',
            'What personal growth have you experienced through this scholarship?',
            'How has this support impacted your educational path?'
          ]
    },
    {
      stage: 3,
      stageName: 'gratitude',
      stageTitle: 'Gratitude & Message to Donors',
      description: 'Share a message of gratitude to the donors, partners, and supporters who make Folds of Honor scholarships possible.',
      tips: [
        'Speak from the heart',
        'Share what this support means to you personally',
        'Remember, donors want to understand your journey and impact'
      ],
      prompts: [
        'Scholarships are made possible by the generosity of donors, partners, and supporters',
        'What would you like to share with them?',
        'Express your gratitude or share what their support means to you and your educational journey'
      ]
    }
  ];
};

export default function MultiStageRecord({ userToken, formData, onAllStagesComplete, onAuthError, onViewMyVideos }) {
  const [showInstruction, setShowInstruction] = useState(true);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState([]); // stage numbers for progress dots
  const [completedStagesData, setCompletedStagesData] = useState([]); // full data with videoUrls
  const [testimonyId, setTestimonyId] = useState(null);

  // Generate stages based on applicant type
  const STAGES = useMemo(() => {
    return getStagesForApplicantType(formData.applicantType || 'first-time');
  }, [formData.applicantType]);

  const currentStage = STAGES[currentStageIndex];
  const progress = ((currentStageIndex) / STAGES.length) * 100;

  /**
   * Handle completion of a single stage
   */
  const handleStageComplete = async (uploadData) => {
    console.log('📹 Stage complete:', {
      stage: currentStage.stage,
      stageName: currentStage.stageName,
      uploadData
    });

    // Store testimony ID from first stage
    if (!testimonyId && uploadData.testimonyId) {
      console.log('💾 Storing testimony ID for future stages:', uploadData.testimonyId);
      setTestimonyId(uploadData.testimonyId);
    }

    // Accumulate full stage data (including videoUrl) for download use
    const stageData = {
      stage: currentStage.stage,
      stageName: currentStage.stageName,
      stageTitle: currentStage.stageTitle,
      videoUrl: uploadData.videoUrl,
      testimonyId: uploadData.testimonyId || testimonyId
    };
    const newCompletedStagesData = [...completedStagesData, stageData];
    setCompletedStagesData(newCompletedStagesData);

    // Mark this stage as completed (numbers for progress dots)
    setCompletedStages(prev => [...prev, currentStage.stage]);

    // Check if this is the last stage
    if (currentStageIndex === STAGES.length - 1) {
      // All stages complete!
      console.log('🎉 All stages completed!');
      onAllStagesComplete({ ...uploadData, allStages: newCompletedStagesData });
    } else {
      // Move to next stage
      setCurrentStageIndex(prev => prev + 1);
    }
  };

  /**
   * Handle going back to previous stage
   */
  const handleBack = () => {
    if (currentStageIndex > 0) {
      setCurrentStageIndex(prev => prev - 1);
    }
  };

  if (showInstruction) {
    return (
      <InstructionStep onContinue={() => setShowInstruction(false)} onViewMyVideos={onViewMyVideos} />
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        {/* Record Component for Current Stage */}
        <RecordStep
        userToken={userToken}
        formData={formData}
        currentStage={currentStage}
        allStages={STAGES}
        currentStageIndex={currentStageIndex}
        completedStages={completedStages}
        progress={progress}
        onUploadComplete={handleStageComplete}
        onAuthError={onAuthError}
        showBackButton={currentStageIndex > 0}
        onBack={handleBack}
        isMultiStage={true}
        existingTestimonyId={testimonyId}
      />
      </div>
    </Layout>
  );
}
